import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TemplateSelector } from '../../components/template-selector/template-selector.component';
import { ApiService } from '../../../_services/api.service';
import { AuthService } from '../../../_services/auth.service';
import { Template } from '../../../_interfaces/template';
import { User } from '../../../_interfaces/user';
import { LoadingSpinnerComponent } from '../../components/loading-spinner/loading-spinner.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DefaultTemplate } from '../../../_interfaces/user-settings.interface';
import { APP_DIALOG_DATA } from '../../components/dialog/dialog.tokens';
import { AppDialogRef } from '../../components/dialog/dialog-ref';
import { DIALOG_IMPORTS } from '../../components/dialog/dialog.imports';

@Component({
  selector: 'app-default-template',
  standalone: true,
  imports: [
    CommonModule,
    ...DIALOG_IMPORTS,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    TemplateSelector,
    MatIconModule,
    LoadingSpinnerComponent,
    TranslateModule
  ],
  templateUrl: './default-template.component.html',
})
export class DefaultTemplateComponent implements OnInit {
  user: User;
  templates: Template[] = [];
  selectedTemplates: Template[] = [];
  originalSelection: DefaultTemplate[] = [];
  isLoading = false;
  isSaving = false;

  constructor(
    @Inject(APP_DIALOG_DATA) public data: any,
    private dialogRef: AppDialogRef<DefaultTemplateComponent>,
    private apiService: ApiService,
    private authService: AuthService,
    private snackBar: MatSnackBar,
    private translate: TranslateService
  ) {
    this.user = this.authService.getUser() as User;
  }

  async ngOnInit() {
    await this.loadTemplates();
    this.loadUserSelection();
  }

  private async loadTemplates(): Promise<void> {
    this.isLoading = true;
    try {
      this.templates = await this.apiService.getCopilotTemplates();
    } catch (error) {
      console.error('Error loading templates:', error);
      this.templates = [];
    } finally {
      this.isLoading = false;
    }
  }

  private loadUserSelection(): void {
    if (this.user?.settings?.copilotTemplates) {
      this.originalSelection = [...this.user.settings.copilotTemplates];
    }
  }

  onSelectionChange(templates: Template[]): void {
    this.selectedTemplates = templates;
  }

  hasSelectionChanged(): boolean {
    if (this.selectedTemplates.length !== this.originalSelection.length) {
      return true;
    }

    const originalMap = new Map(
      this.originalSelection.map(t => [`${t.id}-${t.type}`, t])
    );

    return this.selectedTemplates.some(t => {
      const key = `${t.id}-${t.type}`;
      return !originalMap.has(key);
    });
  }

  async onConfirm(): Promise<void> {
    if (!this.hasSelectionChanged()) {
      this.dialogRef.close();
      return;
    }

    this.isSaving = true;
    let message = this.translate.instant('Blueprints saved successfully.');
    try {
      await this.authService.updateUser({
        settings: {
          copilotTemplates: this.selectedTemplates.map(({ id, type }) => ({ id, type }))
        }
      });
      
      this.dialogRef.close({ templates: this.selectedTemplates });
    } catch (error) {
      message = this.translate.instant('Error saving blueprints');
      console.error('Error saving templates:', error);
    } finally {
      this.isSaving = false;
      this.snackBar.open(message, this.translate.instant('Close'), {
        duration: 2000,
        horizontalPosition: 'right',
        verticalPosition: 'bottom'
      });
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  get showBlueprints(): boolean {
    return this.authService.isAiBlueprintsEnabled;
  }
}
